/**
 * PRESENTER — Orchestrates Model ↔ View.
 * All user-facing logic lives here. addEventListener calls live in view.base.bindStaticEvents.
 */

import * as api from "./api.js";
import { storeAuthToken } from "./api.js";
import {
  showToast,
  showPage,
  setButtonLoading,
  bindStaticEvents,
  updateMapAddress,
} from "./views/view.base.js";
import * as MenuView from "./views/view.menu.js";
import * as InvoiceView from "./views/view.invoice.js";
import * as OtpView from "./views/view.otp.js";
import * as CheckoutView from "./views/view.checkout.js";
import * as SuccessView from "./views/view.success.js";

export class AppPresenter {
  #model;
  #isMenuBuilt = false;
  #otpTimerId = null;
  #trackerTimerId = null;
  #activeAddonProduct = null;
  #statusUnsub = null; // real-time restaurant status cleanup

  constructor(model) {
    this.#model = model;
  }

  // ─── Bootstrap ────────────────────────────────────────────────────────────

  async init() {
    this.#model.restore();

    // Detect gateway redirect (ZarinPal/IDPay return with query params)
    const hasGatewayParams = /Authority|authority|orderId|id=/.test(
      window.location.search
    );
    const hash = window.location.hash.replace("#", "");
    const startPage = hasGatewayParams ? "payment-result" : hash || "menu";

    // Set state without navigation guards on init (restore trusted state)
    this.#model.setState({ currentPage: startPage });
    if (!hash) window.location.hash = startPage;

    // Wire all static event listeners — they call back into presenter methods
    bindStaticEvents({
      onNavigate: (page) => this.#handleDataNavigate(page),
      onAddonSubmit: () => this.#handleAddonSubmit(),
      onAddonClose: () => this.#closeAddon(),
      onClearCart: () => {
        this.#model.clearCart();
        showToast("سبد خرید تخلیه شد", "info");
      },
      onApplyCoupon: () => this.#handleApplyCoupon(),
      onInvoiceSubmit: () =>
        this.navigate(
          this.#model.getState().isAuthenticated ? "checkout" : "otp"
        ),
      onNotesChange: (v) => this.#model.setState({ orderNotes: v }),
      onFloatingCartClick: () => {
        if (!this.#model.getState().restaurantConfig.isOpen) {
          showToast("رستوران هم‌اکنون بسته است.", "error");
          return;
        }
        this.navigate("invoice");
      },
      onSendOtp: () => this.#handleSendOtp(),
      onVerifyOtp: () => this.#handleVerifyOtp(),
      onResendOtp: () => this.#handleResendOtp(),
      onChangePhone: () => {
        clearInterval(this.#otpTimerId);
        this.#model.setState({ otpPhone: null, otpCodeSentAt: null });
      },
      onCheckoutSubmit: () => this.#handleSubmitOrder(),
      onLogout: () => {
        this.#model.logout();
        showToast("با موفقیت خارج شدید", "info");
        this.navigate("menu");
      },
      onRetryPayment: () => this.navigate("checkout"),
      onBackToMenu: () => this.#resetToMenu(),
    });

    // Subscribe to model → re-render
    this.#model.subscribe((state) => this.#onStateChange(state));

    // Expose globals for HTML onclick="" attributes
    window.appNavigate = (page) => this.navigate(page);
    window.resetFlowToMenu = () => this.#resetToMenu();

    // Browser back/forward
    window.addEventListener("hashchange", () => {
      const h = window.location.hash.replace("#", "");
      const curr = this.#model.getState().currentPage;
      if (!h || h === curr) return;
      // Block back-navigation into locked pages
      if (curr === "success") {
        window.location.hash = "menu";
        this.#resetToMenu();
        return;
      }
      this.navigate(h);
    });

    // Initial render
    this.#onStateChange(this.#model.getState());

    // Fetch menu & start real-time status subscription
    await this.#loadMenu();
    this.#subscribeToStatus();
  }

  // ─── data-navigate handler ────────────────────────────────────────────────

  // Called for every element with data-navigate="page" attribute.
  // Some pages need special handling before navigating.
  #handleDataNavigate(page) {
    const state = this.#model.getState();

    // دکمه برگشت در صفحه OTP
    if (page === "otp" && state.currentPage === "otp") {
      // اگه در مرحله کد هستیم، برگرد به مرحله شماره
      if (state.otpPhone && state.otpCodeSentAt) {
        clearInterval(this.#otpTimerId);
        this.#model.setState({ otpPhone: null, otpCodeSentAt: null });
        return;
      }
      // اگه در مرحله شماره هستیم، برگرد به invoice
      this.navigate("invoice");
      return;
    }

    this.navigate(page);
  }

  // ─── Menu ─────────────────────────────────────────────────────────────────

  async #loadMenu() {
    this.#model.setState({ menuLoading: true, menuError: null });
    MenuView.renderLoading();
    try {
      const data = await api.fetchMenu();
      const patch = { menuData: data, menuLoading: false };
      if (data.restaurantConfig) patch.restaurantConfig = data.restaurantConfig;
      this.#model.setState(patch);
      this.#isMenuBuilt = false;
      this.#renderMenu(this.#model.getState());
    } catch (err) {
      console.error("[Presenter] fetchMenu failed:", err);
      this.#model.setState({ menuLoading: false, menuError: err.message });
      MenuView.renderError(err.message || "خطا در دریافت منو.", () =>
        this.#loadMenu()
      );
    }
  }

  #subscribeToStatus() {
    this.#statusUnsub?.();
    this.#statusUnsub = api.subscribeToRestaurantStatus((update) => {
      if (update.type === "STATUS") {
        this.#model.updateRestaurantStatus(update.isOpen);
      } else if (update.type === "PRODUCT") {
        this.#model.updateProductAvailability(update.id, update.available);
      }
    });
  }

  // ─── State Router ─────────────────────────────────────────────────────────

  #onStateChange(state) {
    showPage(state.currentPage);
    switch (state.currentPage) {
      case "menu":
        this.#renderMenu(state);
        break;
      case "invoice":
        this.#renderInvoice(state);
        break;
      case "otp":
        this.#renderOtp(state);
        break;
      case "checkout":
        this.#renderCheckout(state);
        break;
      case "success":
        this.#renderSuccess(state);
        break;
      case "payment-result":
        this.#renderPaymentResult();
        break;
      case "payment-failed":
        this.#renderPaymentFailed(state);
        break;
    }
  }

  // ─── Page Renderers ───────────────────────────────────────────────────────

  #renderMenu(state) {
    if (state.menuLoading || state.menuError || !state.menuData) return;

    MenuView.renderRestaurantStatus(state.restaurantConfig);

    const categories = this.#model.getCategories();
    const products = this.#model.getProducts();

    if (!this.#isMenuBuilt && categories.length > 0) {
      MenuView.buildStructure(categories, products, {
        onTabClick: (id) => {
          MenuView.updateActiveTab(id);
          MenuView.scrollToCategory(id);
        },
        onAddClick: (prod) => this.#onProductAddClick(prod),
        onRemoveClick: (id) => this.#model.removeLastConfigOfProduct(id),
        onConfigAdd: (id, addons) =>
          this.#safeCart(() => this.#model.addToCart(id, addons)),
        onConfigRemove: (id, addons) => this.#model.removeFromCart(id, addons),
      });
      this.#setupIntersectionObserver();
      this.#isMenuBuilt = true;
    }

    products.forEach((prod) => {
      MenuView.updateProductCard(
        prod,
        this.#model.getProductTotalCount(prod.id),
        state.cart.filter((i) => i.productId === prod.id),
        state.restaurantConfig.isOpen,
        {
          onAddClick: (p) => this.#onProductAddClick(p),
          onRemoveClick: (id) => this.#model.removeLastConfigOfProduct(id),
          onConfigAdd: (id, addons) =>
            this.#safeCart(() => this.#model.addToCart(id, addons)),
          onConfigRemove: (id, addons) =>
            this.#model.removeFromCart(id, addons),
        }
      );
    });

    const totalCount = state.cart.reduce((s, i) => s + i.quantity, 0);
    const totalAmount = state.cart.reduce(
      (s, i) => s + i.linePrice * i.quantity,
      0
    );
    MenuView.updateFloatingCart(totalCount, totalAmount);
  }

  #renderInvoice(state) {
    if (
      state.appliedCoupon &&
      this.#model.computeSubtotal() < state.appliedCoupon.minOrder
    ) {
      this.#model.setState({ appliedCoupon: null });
      showToast("کد تخفیف به دلیل کاهش سبد لغو شد", "error");
      return;
    }
    InvoiceView.render(
      state.cart,
      this.#model.getProducts(),
      this.#model.computeFinancials(state.orderType),
      state.orderNotes,
      {
        onItemAdd: (id, addons) =>
          this.#safeCart(() => this.#model.addToCart(id, addons)),
        onItemRemove: (id, addons) => this.#model.removeFromCart(id, addons),
      }
    );
  }

  #renderOtp(state) {
    clearInterval(this.#otpTimerId);
    // const btnBack = document.getElementById("btn-otp-back");

    if (state.otpPhone && state.otpCodeSentAt && !state.otpVerified) {
      OtpView.showCodeStep(state.otpPhone);
      this.#otpTimerId = OtpView.startCountdown(state.otpCodeSentAt, () => {});
      // if (btnBack)
      //   btnBack.onclick = () =>
      //     this.#model.setState({ otpPhone: null, otpCodeSentAt: null });
    } else {
      OtpView.showPhoneStep();
      // if (btnBack) btnBack.onclick = () => this.navigate("invoice");
    }
  }

  #renderCheckout(state) {
    const financials = this.#model.computeFinancials(state.orderType);
    CheckoutView.render(state, financials, {
      onOrderTypeChange: (type) => this.#model.setState({ orderType: type }),
    });
    // Init map picker once per checkout render
    CheckoutView.initMapPicker(state.selectedLocation, (loc) => {
      this.#model.setState({ selectedLocation: loc });
      updateMapAddress(loc.address);
      const addrInput = document.getElementById("receiver-address");
      if (addrInput && loc.address && !addrInput.value)
        addrInput.value = loc.address;
    });
  }

  #renderSuccess(state) {
    if (!state.lastSubmittedOrder) {
      this.navigate("menu");
      return;
    }
    SuccessView.render(state.lastSubmittedOrder, state.orderType);
    clearInterval(this.#trackerTimerId);
    SuccessView.updateTracker(state.lastSubmittedOrder.submittedAt);
    this.#trackerTimerId = setInterval(
      () => SuccessView.updateTracker(state.lastSubmittedOrder.submittedAt),
      2000
    );
  }

  #renderPaymentFailed(state) {
    // page-payment-failed must exist in HTML
    const msgEl = document.getElementById("payment-failed-message");
    if (msgEl)
      msgEl.textContent =
        state.paymentFailedReason || "پرداخت ناموفق بود یا لغو شد.";
    const totalEl = document.getElementById("payment-failed-total");
    if (totalEl && state.pendingOrder)
      totalEl.textContent = `مبلغ: ${state.pendingOrder.financials?.grandTotal?.toLocaleString(
        "fa-IR"
      )} تومان`;
  }

  async #renderPaymentResult() {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("Status") || params.get("status");
    const orderId = params.get("orderId") || params.get("id");
    const authority = params.get("Authority") || params.get("authority");

    if (status !== "OK" && status !== "ok") {
      this.#model.setState({
        currentPage: "payment-failed",
        paymentFailedReason: "پرداخت توسط کاربر لغو شد یا با خطا مواجه شد.",
      });
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}#payment-failed`
      );
      return;
    }

    // Show loading state on success page while verifying
    showPage("success");
    document.getElementById("success-order-id").textContent = "...";

    try {
      const res = await api.verifyPayment({ orderId, authority });
      const s = this.#model.getState();

      this.#model.setState({
        lastSubmittedOrder: {
          id: res.orderId || orderId,
          payable: s.pendingOrder?.financials?.grandTotal || 0,
          receiverName: s.receiverName,
          estimatedMinutes: res.estimatedMinutes || 45,
          submittedAt: Date.now(),
        },
        cart: [],
        appliedCoupon: null,
        couponCode: "",
        orderNotes: "",
        pendingOrder: null,
        currentPage: "success",
      });

      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}#success`
      );
      showToast("پرداخت با موفقیت انجام شد", "success");
    } catch (err) {
      this.#model.setState({
        currentPage: "payment-failed",
        paymentFailedReason: err.message || "خطا در تأیید پرداخت",
      });
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}#payment-failed`
      );
    }
  }

  // ─── Navigation ───────────────────────────────────────────────────────────

  navigate(page) {
    const guard = this.#model.canNavigateTo(page);
    if (guard) {
      if (guard.reason) showToast(guard.reason, "error");
      if (guard.redirect) {
        // Silently redirect without re-checking guard (avoid infinite loop)
        this.#model.setState({ currentPage: guard.redirect });
        window.location.hash = guard.redirect;
      }
      return;
    }
    this.#model.setState({ currentPage: page });
    window.location.hash = page;
  }

  // ─── Product Add ──────────────────────────────────────────────────────────

  #onProductAddClick(prod) {
    if (prod.afzoodanis?.length > 0) {
      this.#activeAddonProduct = prod;
      MenuView.openAddonSheet(prod, (selectedAddons) => {
        MenuView.updateSheetTotalPrice(
          this.#model.calculateLinePrice(prod, selectedAddons)
        );
      });
    } else {
      this.#safeCart(() => this.#model.addToCart(prod.id));
    }
  }

  #handleAddonSubmit() {
    if (!this.#activeAddonProduct) return;
    this.#safeCart(() =>
      this.#model.addToCart(
        this.#activeAddonProduct.id,
        MenuView.getSelectedAddons()
      )
    );
    this.#closeAddon();
  }

  #closeAddon() {
    MenuView.closeAddonSheet();
    this.#activeAddonProduct = null;
  }

  #safeCart(fn) {
    try {
      fn();
    } catch (err) {
      showToast(err.message, "error");
    }
  }

  // ─── Intersection Observer ────────────────────────────────────────────────

  #setupIntersectionObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting)
            MenuView.updateActiveTab(e.target.id.replace("section-", ""));
        });
      },
      { root: null, rootMargin: "-80px 0px -50% 0px", threshold: 0 }
    );
    document
      .querySelectorAll(".menu-category-section")
      .forEach((s) => observer.observe(s));
  }

  // ─── OTP Handlers ─────────────────────────────────────────────────────────

  async #handleSendOtp() {
    const phone = document.getElementById("phone-input")?.value.trim() || "";
    if (!/^09\d{9}$/.test(phone)) {
      showToast("لطفا شماره همراه معتبر ۱۱ رقمی وارد کنید", "error");
      return;
    }
    setButtonLoading("btn-send-otp", true);
    try {
      await api.sendOTP(phone);
      this.#model.setState({ otpPhone: phone, otpCodeSentAt: Date.now() });
      showToast("کد تایید (۱۲۳۴) ارسال شد", "success");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setButtonLoading(
        "btn-send-otp",
        false,
        `<span>ارسال کد تایید</span> <i class="fa-solid fa-paper-plane"></i>`
      );
    }
  }

  async #handleVerifyOtp() {
    const code = OtpView.getCode();
    if (code.length < 4) {
      showToast("لطفا کد ۴ رقمی را کامل کنید", "error");
      return;
    }
    setButtonLoading("btn-verify-otp", true);
    try {
      const res = await api.verifyOTP(this.#model.getState().otpPhone, code);
      const verifiedPhone = this.#model.getState().otpPhone;
      storeAuthToken(res.token);
      this.#model.setState({
        otpVerified: true,
        isAuthenticated: true,
        authToken: res.token || null,
        authenticatedPhone: verifiedPhone,
        receiverPhone: this.#model.getState().receiverPhone || verifiedPhone,
        otpPhone: null,
        otpCodeSentAt: null,
      });
      OtpView.clearDigits();
      showToast("ورود موفق", "success");
      this.navigate("checkout");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setButtonLoading(
        "btn-verify-otp",
        false,
        `<span>تایید و ورود</span> <i class="fa-solid fa-check"></i>`
      );
    }
  }

  async #handleResendOtp() {
    const btn = document.getElementById("btn-resend-otp");
    if (btn) btn.disabled = true;
    try {
      await api.sendOTP(this.#model.getState().otpPhone);
      this.#model.setState({ otpCodeSentAt: Date.now() });
      showToast("کد جدید ارسال شد", "success");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  // ─── Coupon ───────────────────────────────────────────────────────────────

  async #handleApplyCoupon() {
    const code = document.getElementById("coupon-input")?.value || "";
    try {
      await this.#model.applyCoupon(code);
      InvoiceView.setCouponMessage("کد تخفیف اعمال شد", true);
      showToast("کد تخفیف ثبت شد", "success");
    } catch (err) {
      InvoiceView.setCouponMessage(err.message, false);
      showToast(err.message, "error");
    }
  }

  // ─── Checkout / Order Submit ──────────────────────────────────────────────

  async #handleSubmitOrder() {
    const { name, phone, address } = CheckoutView.getFormValues();
    const state = this.#model.getState();
    const financials = this.#model.computeFinancials(state.orderType);

    if (!name) {
      showToast("لطفا نام تحویل‌گیرنده را وارد کنید", "error");
      return;
    }
    if (!/^09\d{9}$/.test(phone)) {
      showToast("لطفا شماره تماس معتبری وارد کنید", "error");
      return;
    }
    if (state.orderType === "delivery" && !address) {
      showToast("لطفا آدرس تحویل را وارد کنید", "error");
      return;
    }

    // Full order payload — includes everything backend needs
    const payload = {
      clientPhone: state.authenticatedPhone,
      receiver: { name, phone },
      orderType: state.orderType,
      address:
        state.orderType === "delivery" ? address : "تحویل حضوری در محل رستوران",
      location:
        state.orderType === "delivery" ? state.selectedLocation || null : null,
      items: state.cart.map(
        ({ productId, quantity, selectedAddons, linePrice }) => ({
          productId,
          quantity,
          selectedAddons,
          linePrice,
        })
      ),
      orderNotes: state.orderNotes,
      couponCode: state.couponCode,
      financials,
    };

    setButtonLoading("btn-checkout-submit", true);
    try {
      const res = await api.submitOrder(payload);

      if (res.paymentUrl) {
        // Save full order payload so we can access it after gateway callback
        this.#model.setState({ pendingOrder: payload });
        window.location.href = res.paymentUrl;
        return;
      }

      // No gateway redirect — order completed directly
      this.#model.setState({
        receiverName: name,
        receiverPhone: phone,
        receiverAddress: state.orderType === "delivery" ? address : "",
        lastSubmittedOrder: {
          id: res.orderId,
          payable: financials.grandTotal,
          receiverName: name,
          estimatedMinutes: res.estimatedMinutes,
          submittedAt: Date.now(),
        },
        cart: [],
        appliedCoupon: null,
        couponCode: "",
        orderNotes: "",
        pendingOrder: null,
      });

      showToast("سفارش با موفقیت ثبت شد", "success");
      this.navigate("success");
    } catch (err) {
      showToast(err.message || "خطا در ثبت سفارش", "error");
    } finally {
      setButtonLoading(
        "btn-checkout-submit",
        false,
        `<span>پرداخت و ثبت نهایی</span> <i class="fa-solid fa-credit-card"></i>`
      );
    }
  }

  // ─── Reset ────────────────────────────────────────────────────────────────

  #resetToMenu() {
    clearInterval(this.#trackerTimerId);
    this.#model.setState({
      lastSubmittedOrder: null,
      paymentFailedReason: null,
    });
    this.navigate("menu");
  }
}
